import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { Modal } from '@tailwindComponents/Modal';
import { Button } from '@tailwindComponents/Button';
import { Customer } from './customerTypes';

export interface CustomerArchiveModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (customer: Customer, isArchiving: boolean) => void;
}

export function CustomerArchiveModal({
  customer,
  isOpen,
  onClose,
  onConfirm,
}: CustomerArchiveModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={onClose}
      title={`${customer?.isArchived ? 'Unarchive' : 'Archive'} customer ${
        customer?.name ?? ''
      }?`}
    >
      {customer && (
        <div className='flex flex-col gap-6 p-4 text-gray-600'>
          {!customer.isArchived && (
            <>
              <p>You&apos;re about to archive {customer.name}.</p>
              <p className='max-w-2xl'>
                Archiving will remove this customer from active customer views
                and pickers, but all their records and order history will remain
                securely stored in FLOX.
              </p>
              <p>You&apos;ll still be able to:</p>
              <ul className='list-disc ml-6'>
                <li>See them in existing despatch notes and orders</li>
                <li>Find them with the &quot;Include archived&quot; filter</li>
                <li>Restore them to active status at any time</li>
              </ul>
              <p className='p-6 m-2 mb-4 bg-sky-100 text-sky-800'>
                <InformationCircleIcon className='inline h-5 w-5 mr-2' />
                This action won&apos;t delete any data or external references.
              </p>
              <div className='flex flex-row gap-2 justify-center'>
                <Button
                  intent='secondary'
                  label='Cancel'
                  size='xl'
                  onClick={onClose}
                />
                <Button
                  intent='danger'
                  label='Archive customer'
                  size='xl'
                  onClick={() => {
                    onConfirm(customer, true);
                    onClose();
                  }}
                />
              </div>
            </>
          )}
          {customer.isArchived && (
            <div className='flex flex-col gap-6 p-4 text-gray-600 max-w-2xl'>
              <p>You&apos;re about to unarchive {customer.name}.</p>
              <p>
                This action will restore this customer to active customer views,
                making them available for despatches and orders again.
              </p>
              <div className='flex flex-row gap-2 justify-center'>
                <Button
                  intent='secondary'
                  label='Cancel'
                  size='xl'
                  onClick={onClose}
                />
                <Button
                  intent='primary'
                  label='Unarchive customer'
                  size='xl'
                  onClick={() => {
                    onConfirm(customer, false);
                    onClose();
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
